import { inject, Injectable, Injector } from '@angular/core';
import { ApiBoardSettingsResponse, ApiListActionStatusesResponse } from '../../api/core/models';
import { DashboardSettingsResponse } from '../../api/core/models/api/dashboard-settings-response';
import { BoardSettings } from '../../model/board-settings';
import { ClientBoard } from '../../model/client-model/client-board';
import { Layout } from '../../model/client-model/settings/board-settings/layout';
import { PointOfEntrySettings } from '../../model/client-model/settings/point-of-entry/point-of-entry-settings';
import { DefaultActionsView } from '../../model/default-actions-view';
import { PublishedBoardV3_1Data, PublishedBoardV4_1Data } from '../backend/backend-published-board.types';
import { LoggingService } from '../logging.service';
import {
  apiBoardSettingsToMeasurementSystem,
  apiBoardSettingsToSwimlanes,
  displayUnitExponentToDisplayUnit,
  emissionStoryToEmissionPathways,
  mainStoryToExtendedDescription,
  transitionElementLayoutToTransitionTargetNodes,
} from './helpers';

type ClientBoardV3_1 = Omit<ClientBoard, '_etag' | '_ts' | 'boardSettings' | 'title'> & {
  boardSettings: Omit<
    BoardSettings,
    | 'goalTitle'
    | 'goalDescription'
    | 'reductionGoals'
    | 'emissionStory'
    | 'imgURL'
    | 'auxiliaryActionProperties'
    | 'electricityEmissionFactorType'
    | 'publicTabs'
  >;
};

type ClientBoardV4_1 = Omit<ClientBoard, '_etag' | '_ts' | 'boardSettings' | 'title'> & {
  boardSettings: Omit<
    BoardSettings,
    | 'auxiliaryActionProperties'
    | 'imgURL'
    | 'doNotDistributeUntargeted'
    | 'unaddressedEmissionsDisplay'
    | 'useNewTargetedEmissions'
    | 'electricityEmissionFactorType'
  >;
};

type ClientBoardV3_1And4_1Shared = Omit<ClientBoard, '_etag' | '_ts' | 'boardSettings' | 'title'> & {
  boardSettings: Omit<
    BoardSettings,
    | 'auxiliaryActionProperties'
    | 'imgURL'
    | 'goalTitle'
    | 'goalDescription'
    | 'reductionGoals'
    | 'emissionStory'
    | 'doNotDistributeUntargeted'
    | 'unaddressedEmissionsDisplay'
    | 'useNewTargetedEmissions'
    | 'electricityEmissionFactorType'
    | 'publicTabs'
  >;
};

@Injectable({ providedIn: 'root' })
export class PublishedBoardsModelService {
  private _injector = inject(Injector);

  publishedV3_1BoardToClientBoard(boardData: PublishedBoardV3_1Data): ClientBoardV3_1 {
    const sharedBoardProperties = this._publishedV3_1And4_1BoardToSharedClientBoardProperties(boardData);

    return {
      ...sharedBoardProperties,
      boardSettings: {
        ...sharedBoardProperties.boardSettings,
        extendedDescription: mainStoryToExtendedDescription(boardData.mainStory),
        useNewTargetedEmissions: boardData.useNewTargetedEmissions,
        doNotDistributeUntargeted: boardData.doNotDistributeUntargetedEmissions,
        unaddressedEmissionsDisplay: boardData.unaddressedEmissionsDisplay,
        totalEmissions: boardData.totalEmissions,
        transitionTargetNodes: {
          showCardImages: boardData.transitionElementShowCardImages,
          layout:
            boardData.transitionElementLayout === 'GRAPH_AND_BAROMETER'
              ? Layout.GRAPH_AND_BAROMETER
              : boardData.transitionElementLayout === 'TARGET_AND_GRAPH'
                ? Layout.TARGET_AND_GRAPH
                : Layout.TARGET_AND_TEXT,
        },
        yearByYearDiagramLayout: {
          showBauAndAbatement: boardData.showReferenceScenarioAndAbatement ?? false,
        },
      },
    };
  }

  publishedV4_1BoardToClientBoard(boardData: PublishedBoardV4_1Data): ClientBoardV4_1 {
    const sharedBoardProperties = this._publishedV3_1And4_1BoardToSharedClientBoardProperties(boardData);

    return {
      ...sharedBoardProperties,
      boardSettings: {
        actionOrigins: boardData.actionOrigins,
        ...sharedBoardProperties.boardSettings,
        extendedDescription: mainStoryToExtendedDescription(boardData.mainStory),
        goalTitle: boardData.goalTitle,
        reductionGoals: boardData.reductionGoals,
        emissionsPathways: emissionStoryToEmissionPathways(boardData.emissionStory),
        pointOfEntrySettings: <PointOfEntrySettings>{
          ...sharedBoardProperties.boardSettings.pointOfEntrySettings,
          header: {
            ...sharedBoardProperties.boardSettings.pointOfEntrySettings?.header,
            taglineSubText: boardData.goalDescription,
          },
        },
        publicTabs: boardData.publicTabs ?? {
          emissions: true,
          transitions: true,
          interventions: true,
          indicators: true,
        },
        primaryActionGrouping: boardData.primaryActionGrouping,
        secondaryActionGrouping: boardData.secondaryActionGrouping,
        actionSettingsSubHeading: boardData.actionSettingsSubHeading,
        actionSettingsDescription: boardData.actionSettingsDescription,
        emissionsTabHideBracket: boardData.emissionsTabHideBracket,
        emissionsTabHideReference: boardData.emissionsTabHideReference,
        headerLogoUrl: boardData.headerLogoUrl,
        indicatorsHideContribution: boardData.indicatorsHideContribution,
      },
    };
  }

  private _publishedV3_1And4_1BoardToSharedClientBoardProperties(
    boardData: PublishedBoardV3_1Data | PublishedBoardV4_1Data
  ): ClientBoardV3_1And4_1Shared {
    const loggingService = this._injector.get(LoggingService);
    return {
      id: boardData.boardId,
      childNodes: boardData.tempTopNodes?.nodes ?? [],
      supplyChildNodes: boardData.tempTopNodes?.supplyNodes ?? [],
      boardSettings: {
        actionTags: boardData.actionTags?.map((actionTag) => ({
          id: actionTag.id,
          title: actionTag.displayValue,
          colour: actionTag.colour,
        })),
        actionTypes: boardData.actionTypes,
        swimlanes: apiBoardSettingsToSwimlanes({
          actionStatuses: boardData.actionStatuses,
        } as ApiListActionStatusesResponse),
        feedbackSettings: boardData.feedbackSettings,
        internationalisation: {
          defaultLanguage: boardData.primaryLanguage,
        },
        defaultActionsView: boardData.actionSettingsLayout?.toLowerCase() as DefaultActionsView,
        showUnconnectedActions: boardData.actionSettingsShowUnconnected,
        showActionsCosts: boardData.actionSettingsShowCosts,
        transitionTargetNodes: transitionElementLayoutToTransitionTargetNodes({
          transitionElementLayout: boardData.transitionElementLayout,
          transitionElementShowCardImages: boardData.transitionElementShowCardImages,
        } as DashboardSettingsResponse),
        introductionTitle: boardData.splashScreenTitle,
        introductionSummary: boardData.splashScreenDescription,
        pointOfEntrySettings: {
          header: {
            backgroundImageCredits: boardData.splashScreenBackgroundImageCredits,
            backgroundImage: {
              large: boardData.splashScreenBackgroundImageUrl,
            },
            country: boardData.rootLocation,
            about: boardData.about,
            tagline: boardData.aboutTitle,
            boardTitle: boardData.title,
          },
          footer: {
            bodyLeft: boardData.footerBodyLeft,
            bodyRight: boardData.footerBodyRight,
            images:
              boardData.footerLogos?.map((logo) => ({
                sourceURL: logo.imageUrl,
                id: logo.id,
                ...(logo.destinationUrl ? { destinationURL: logo.destinationUrl } : {}),
              })) ?? [],
          },
        },
        timeline: boardData.timeline,
        measurementSystem: apiBoardSettingsToMeasurementSystem({
          defaultMeasurementSystem: boardData.defaultMeasurementSystem,
        } as ApiBoardSettingsResponse),
        currency: boardData.currency,
        displayUnit: displayUnitExponentToDisplayUnit(loggingService, Number(boardData.displayUnitExponent)),
        rootNodeProperties: {
          diagramHeader: boardData.mainStory?.diagramHeader ?? '',
        },
      },
    };
  }
}
